import React, { createContext, useContext, useState, useCallback } from 'react';
import { managementAPI } from '../services/api';

const DataContext = createContext();

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState({});
  const [shiftsCache, setShiftsCache] = useState({});
  const [shiftsLoading, setShiftsLoading] = useState({});

  const getEmployeesPageBundle = useCallback(async (companyId) => {
    if (!companyId) return null;

    const cacheKey = `employees_bundle_${companyId}`;
    
    // Return cached data if available
    if (cache[cacheKey] && cache[cacheKey].timestamp > Date.now() - 30000) { // 30 seconds cache
      // console.log('[DataContext] Returning cached data for companyId:', companyId);
      return cache[cacheKey].data;
    }

    // Prevent duplicate API calls
    if (loading[cacheKey]) {
      // console.log('[DataContext] Already loading, waiting for result...');
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!loading[cacheKey] && cache[cacheKey]) {
            resolve(cache[cacheKey].data);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    try {
      console.log('[DataContext] Calling employeesPageBundleLight for companyId:', companyId);
      setLoading(prev => ({ ...prev, [cacheKey]: true }));
      
      const res = await managementAPI.employeesPageBundleLight(Number(companyId));
      const data = res.data || {};

      setCache(prev => ({
        ...prev,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      }));

      return data;
    } finally {
      setLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [cache, loading]);

  const getEmployeeShiftsDetail = useCallback(async (employeeUserId, siteId) => {
    if (!employeeUserId || !siteId) return null;

    const cacheKey = `shifts_${employeeUserId}_${siteId}`;
    
    // Return cached data if available
    if (shiftsCache[cacheKey] && shiftsCache[cacheKey].timestamp > Date.now() - 30000) { // 30 seconds cache
      return shiftsCache[cacheKey].data;
    }

    // Prevent duplicate API calls
    if (shiftsLoading[cacheKey]) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!shiftsLoading[cacheKey] && shiftsCache[cacheKey]) {
            resolve(shiftsCache[cacheKey].data);
          } else {
            setTimeout(checkLoading, 100);
          }
        };
        checkLoading();
      });
    }

    try {
      console.log('[DataContext] Calling employeeShiftsDetail for employee:', employeeUserId, 'site:', siteId);
      setShiftsLoading(prev => ({ ...prev, [cacheKey]: true }));
      
      const res = await managementAPI.employeeShiftsDetail(employeeUserId, siteId);
      const data = res.data || {};

      setShiftsCache(prev => ({
        ...prev,
        [cacheKey]: {
          data,
          timestamp: Date.now()
        }
      }));

      return data;
    } finally {
      setShiftsLoading(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [shiftsCache, shiftsLoading]);

  const invalidateCache = useCallback((companyId) => {
    if (companyId) {
      const cacheKey = `employees_bundle_${companyId}`;
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[cacheKey];
        return newCache;
      });
      // Also clear related shifts cache
      setShiftsCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(key => {
          if (key.includes('shifts_')) {
            delete newCache[key];
          }
        });
        return newCache;
      });
    } else {
      setCache({});
      setShiftsCache({});
    }
  }, []);

  const value = {
    getEmployeesPageBundle,
    getEmployeeShiftsDetail,
    invalidateCache,
    isLoading: (companyId) => loading[`employees_bundle_${companyId}`] || false,
    isShiftsLoading: (employeeUserId, siteId) => shiftsLoading[`shifts_${employeeUserId}_${siteId}`] || false,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};